import { z } from 'zod';
import { IdSchema, TimestampSchema } from './common.js';

export const DocumentKindSchema = z.enum(['brand_voice', 'knowledge_base', 'example_thread']);
export type DocumentKind = z.infer<typeof DocumentKindSchema>;

export const DocumentSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(200),
  kind: DocumentKindSchema,
  topicId: IdSchema.nullable(),
  content: z.string(),
  originalFileKey: z.string().nullable(),
  originalFilename: z.string().nullable(),
  alwaysInclude: z.boolean(),
  isActive: z.boolean(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Document = z.infer<typeof DocumentSchema>;

// Pasted text path (no file upload)
export const CreateDocumentSchema = z
  .object({
    name: z.string().min(1).max(200),
    kind: DocumentKindSchema,
    topicId: IdSchema.nullable().optional(),
    content: z.string().min(1),
    alwaysInclude: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (d) => (d.kind === 'example_thread' ? !!d.topicId : true),
    'example_thread documents must be attached to a topic',
  );
export type CreateDocumentInput = z.infer<typeof CreateDocumentSchema>;

export const UpdateDocumentSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  topicId: IdSchema.nullable().optional(),
  content: z.string().optional(),
  alwaysInclude: z.boolean().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateDocumentInput = z.infer<typeof UpdateDocumentSchema>;

// Multipart upload extraction preview response
export const ExtractionPreviewSchema = z.object({
  filename: z.string(),
  mimeType: z.string(),
  extractedText: z.string(),
  charCount: z.number().int().min(0),
  looksEmpty: z.boolean(), // heuristic flag for suspiciously short text
  uploadKey: z.string(), // temporary R2 key the manager confirms to keep
});
export type ExtractionPreview = z.infer<typeof ExtractionPreviewSchema>;

export const ListDocumentsQuerySchema = z.object({
  kind: DocumentKindSchema.optional(),
  topicId: IdSchema.optional(),
  includeInactive: z.coerce.boolean().default(false),
});
export type ListDocumentsQuery = z.infer<typeof ListDocumentsQuerySchema>;
